import { Alert, AlertIcon, Box, Text, VStack, WhiteCard } from '@elkaso-app/web-design';

interface DiscrepanciesSectionProps {
  discrepancies: Record<string, any>[] | null;
  validationErrors: (string | Record<string, any>)[] | null;
  processingError: string | null;
}

const formatValidationError = (error: Record<string, any>): string => {
  if (error.message) return String(error.message);
  const field = error.field ? `${error.field}: ` : '';
  if (error.orderValue !== undefined && error.invoiceValue !== undefined) {
    return `${field}order value ${error.orderValue} vs invoice value ${error.invoiceValue}`;
  }
  return `${field}${JSON.stringify(error)}`;
};

const renderCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const DiscrepanciesSection = ({
  discrepancies,
  validationErrors,
  processingError,
}: DiscrepanciesSectionProps) => {
  const hasDiscrepancies = discrepancies && discrepancies.length > 0;
  const hasValidationErrors = validationErrors && validationErrors.length > 0;
  const hasProcessingError = !!processingError;

  if (!hasDiscrepancies && !hasValidationErrors && !hasProcessingError) return null;

  return (
    <VStack spacing='md' align='stretch'>
      {hasProcessingError && (
        <Alert status='error' borderRadius='md'>
          <AlertIcon />
          <Box>
            <Text fontWeight='semibold'>Processing Error</Text>
            <Text fontSize='sm'>{processingError}</Text>
          </Box>
        </Alert>
      )}

      {hasValidationErrors && (
        <WhiteCard p='lg'>
          <Text fontSize='lg' fontWeight='semibold' mb='md' color='red.600'>
            Validation Errors
          </Text>
          <VStack spacing='xs' align='start'>
            {validationErrors!.map((error, i) => (
              <Text key={i} fontSize='sm' color='red.600'>
                • {typeof error === 'string' ? error : formatValidationError(error)}
              </Text>
            ))}
          </VStack>
        </WhiteCard>
      )}

      {hasDiscrepancies && (
        <WhiteCard p='lg'>
          <Text fontSize='lg' fontWeight='semibold' mb='md' color='orange.600'>
            Discrepancies ({discrepancies!.length})
          </Text>
          <Box overflowX='auto'>
            <Box as='table' w='100%' fontSize='sm'>
              <Box as='thead'>
                <Box as='tr'>
                  <Box
                    as='th'
                    textAlign='left'
                    px='md'
                    py='sm'
                    borderBottom='1px'
                    borderColor='gray.200'
                    color='gray.500'
                    fontSize='xs'
                    fontWeight='semibold'
                    textTransform='uppercase'>
                    Type
                  </Box>
                  <Box
                    as='th'
                    textAlign='left'
                    px='md'
                    py='sm'
                    borderBottom='1px'
                    borderColor='gray.200'
                    color='gray.500'
                    fontSize='xs'
                    fontWeight='semibold'
                    textTransform='uppercase'>
                    Field
                  </Box>
                  <Box
                    as='th'
                    textAlign='left'
                    px='md'
                    py='sm'
                    borderBottom='1px'
                    borderColor='gray.200'
                    color='gray.500'
                    fontSize='xs'
                    fontWeight='semibold'
                    textTransform='uppercase'>
                    Expected
                  </Box>
                  <Box
                    as='th'
                    textAlign='left'
                    px='md'
                    py='sm'
                    borderBottom='1px'
                    borderColor='gray.200'
                    color='gray.500'
                    fontSize='xs'
                    fontWeight='semibold'
                    textTransform='uppercase'>
                    Actual
                  </Box>
                  <Box
                    as='th'
                    textAlign='left'
                    px='md'
                    py='sm'
                    borderBottom='1px'
                    borderColor='gray.200'
                    color='gray.500'
                    fontSize='xs'
                    fontWeight='semibold'
                    textTransform='uppercase'>
                    Difference
                  </Box>
                  <Box
                    as='th'
                    textAlign='left'
                    px='md'
                    py='sm'
                    borderBottom='1px'
                    borderColor='gray.200'
                    color='gray.500'
                    fontSize='xs'
                    fontWeight='semibold'
                    textTransform='uppercase'>
                    Message
                  </Box>
                </Box>
              </Box>
              <Box as='tbody'>
                {discrepancies!.map((d, i) => (
                  <Box as='tr' key={i} _hover={{ bg: 'gray.50' }}>
                    <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
                      <Text color='orange.600' fontSize='xs' fontWeight='semibold'>
                        {d.type ? String(d.type).replace(/_/g, ' ') : '—'}
                      </Text>
                    </Box>
                    <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
                      {renderCellValue(d.field)}
                    </Box>
                    <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
                      {renderCellValue(d.expected ?? d.orderValue)}
                    </Box>
                    <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
                      {renderCellValue(d.actual ?? d.invoiceValue)}
                    </Box>
                    <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
                      {renderCellValue(d.difference ?? d.percentageDiff)}
                    </Box>
                    <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
                      {renderCellValue(d.message)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </WhiteCard>
      )}
    </VStack>
  );
};
